import { ErrorResponse, GeocodeContext, GeocodeContextList } from '@citruscamps/citrus-client'
import { Dispatch, useState } from 'react'
import { useQuery } from 'react-query'
import { DefaultMainPagination, UnlimitedPagination } from '../constants/pagination'
import { useSearchValue } from '../hooks/useSearchValue'
import { Pagination } from '../interfaces/pagination'
import { HttpClient } from '../utils/http'
import { generateListKey } from '../utils/key-generator'
import { toQueryParams } from '../utils/query-params'
import { useRequestHandler } from './useRequestHandler'

const DefaultFetchProps: FetchProps = {
  sort: 'created',
  order: 'DESC',
  filter: {},
}

interface FetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: {}
}

interface IProps {
  pagination?: Pagination
  fetchProps?: FetchProps
}

interface FetchCountries {
  data: GeocodeContext[]
  error: ErrorResponse | null
  fetchProps: FetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchCountries = ({
  pagination: initialPagination = UnlimitedPagination,
  fetchProps = DefaultFetchProps,
}: IProps = {}): FetchCountries => {
  const { requestHandler } = useRequestHandler()
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('', {
    onDefer: () => setPagination(initialPagination || DefaultMainPagination),
  })
  const queryKeys = generateListKey({
    type: 'country',
    program_id: '',
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<GeocodeContextList, ErrorResponse>(queryKeys, async () => {
    const skip = pagination.limit ? pagination.page * pagination.limit : undefined
    const client = new HttpClient()
    const q = toQueryParams({
      limit: pagination.limit,
      skip,
      order: fetchProps.order,
      sort: fetchProps.sort,
      deferSearch,
    })
    return await requestHandler<GeocodeContextList>(() =>
      client.get('/api/v1/countries' + (q ? `?${q}` : '')),
    )
  })

  return {
    data: resp?.data || [],
    error,
    fetchProps,
    isError,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    setPagination,
    setSearch,
  }
}
